import React, { useState, useMemo } from 'react'
import {
  Card,
  Typography,
  Tag,
  Space,
  Button,
  Divider,
  Modal,
  Row,
  Col,
  Input,
  message,
  Table,
  Popconfirm,
} from 'antd'
import { TagOutlined } from '@ant-design/icons'
import {
  AddKeyWordAPI,
  DeleteKeyWordAPI,
  DeleteKeyWordGroupAPI,
  AddKeyWordGroupAPI,
} from '@services/quality-inspection'
import { KeyWordItemType, KeyWordTypeEnum } from '@types'
import { useKeyWordsList } from '@hooks'

interface PropsType {
  type: KeyWordTypeEnum
};

const titleMap = {
  [KeyWordTypeEnum.client]: '客户关键词',
  [KeyWordTypeEnum.customerService]: '客服关键词',
}

const defOkButtonProps = { loading: false, disabled: false }

const KeyWordItem: React.FC<PropsType> = (props) => {
  const { type } = props
  const { getKeyWordList, maxKeyWordCnt = 50, groupsList } = useKeyWordsList()

  // 关键词弹窗
  const [isOpen, setIsOpen] = useState(false)
  // 关键词名称
  const [keyWord, setKeyWord] = useState('')
  // 分组弹窗
  const [isGroupOpen, setIsGroupOpen] = useState(false)
  // 分组名称
  const [groupName, setGroupName] = useState('')
  // 分组ID
  const [groupId, setGroupId] = useState(null)
  const [okButtonProps, setOkButtonProps] = useState(defOkButtonProps)
  // 关键词列表 分组列表
  const groupList = useMemo(() => {
    return groupsList?.[type] ?? []
  }, [type, groupsList])
  // 是否禁用相关按钮 所有分组的关键词总数
  const [isDisabled, total] = useMemo(() => {
    // 所有分组的关键词总数
    const total = groupList.map(i => (i?.list?.length ?? 0))?.reduce((acc, num) => acc + num, 0) ?? 0
    return [total === maxKeyWordCnt, total]
  }, [groupList, maxKeyWordCnt])

  // 表格列
  const columns = [
    {
      title: '分组',
      dataIndex: 'groupName',
      key: 'groupName',
      width: '20%',
    },
    {
      title: '关键词',
      dataIndex: 'list',
      key: 'list',
      width: '60%',
      render: (list: KeyWordItemType[]) => {
        return (
          <>
            {list?.map(({ value, id }) => {
              return (
                <Tag
                  className="key-word-tag"
                  icon={<TagOutlined rotate={-90} />}
                  color="#ececec"
                  key={id}
                  closable
                  onClose={() => handleUpdateKeyWord('del', id)}
                >
                  <Typography.Text style={{ maxWidth: 100 }} ellipsis={{ tooltip: value }}>{value}</Typography.Text>
                </Tag>
              )
            })}
          </>
        )
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            disabled={isDisabled}
            onClick={() => {
              setIsOpen(true)
              setGroupId(record.groupId)
            }}
          >新建关键词
          </Button>
          <Popconfirm
            title="确定删除该分组?"
            onConfirm={() => handleUpdateGroup('del', record.groupId)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small">删除分组</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  /**
   * 更新关键词
   * @param uType 新增 / 删除
   * @param id 关键词id(新增为空)
   */
  const handleUpdateKeyWord = async (uType: 'add' | 'del', id?: number) => {
    if (uType === 'add' && (!keyWord || typeof groupId !== 'number')) {
      message.error(`${titleMap[type]}不能为空！`)
      return
    }
    let err
    setOkButtonProps({ loading: true, disabled: true })
    if (uType === 'add') {
      [, err] = await AddKeyWordAPI({ value: keyWord, type, groupId })
    } else {
      [, err] = await DeleteKeyWordAPI(id)
    }
    setOkButtonProps(defOkButtonProps)
    if (err) {
      message.error(err.message)
    } else {
      message.success(`${uType === 'add' ? '新增' : '删除'}${titleMap[type]}成功`)
      handleCancel()
      getKeyWordList()
    }
  }

  /**
   * 关闭新增弹窗 - 重置数据
   */
  const handleCancel = () => {
    setIsOpen(false)
    setIsGroupOpen(false)
    setKeyWord('')
    setGroupName('')
    setGroupId(null)
    setOkButtonProps(defOkButtonProps)
  }

  /**
   * 更新分组 新增/删除
   * @param type 新增 / 删除
   * @param groupId 分组ID
   */
  const handleUpdateGroup = async (uType: 'add' | 'del', groupId?: number) => {
    if (uType === 'add' && !groupName) {
      message.error('分组名称不能为空！')
      return
    }
    let err
    if (uType === 'add') {
      [, err] = await AddKeyWordGroupAPI({ name: groupName, type })
    }
    if (uType === 'del') {
      [, err] = await DeleteKeyWordGroupAPI(groupId)
    }
    if (err) {
      message.error(err.message)
    } else {
      message.success(`${uType === 'add' ? '新增' : '删除'}分组成功`)
      handleCancel()
      getKeyWordList()
    }
  }

  return (
    <div className="key-word-item">
      <Card
        title={
          <>
            <Space size="large">
              <Typography.Text strong style={{ fontSize: 14 }}>{titleMap[type]}</Typography.Text>
              <span className={isDisabled && 'label-disabled'}>{`${total}/${maxKeyWordCnt}`}</span>
            </Space>
            <Button type="primary" disabled={isDisabled} onClick={() => setIsGroupOpen(true)}>新建分组</Button>
          </>
        }
        bordered={false}
      >
        <Table columns={columns} dataSource={groupList} />
      </Card>
      <Divider />
      {/* 新建关键词 */}
      <Modal
        maskClosable={false}
        keyboard={false}
        title={`新建${titleMap[type]}`}
        open={isOpen}
        onOk={() => handleUpdateKeyWord('add')}
        okButtonProps={okButtonProps}
        onCancel={handleCancel}
      >
        <Row>
          <Col span={8}>关键词名称：</Col>
          <Col span={16}>
            <Input
              value={keyWord}
              onChange={e => setKeyWord(e.target.value.trim())}
              placeholder="请输入关键词名称"
              maxLength={128}
            />
          </Col>
        </Row>
      </Modal>
      {/* 新建关键词分组 */}
      <Modal
        maskClosable={false}
        keyboard={false}
        title={`新建${titleMap[type]}分组`}
        open={isGroupOpen}
        onOk={() => handleUpdateGroup('add')}
        okButtonProps={okButtonProps}
        onCancel={handleCancel}
      >
        <Row>
          <Col span={8}>分组名称：</Col>
          <Col span={16}>
            <Input
              value={groupName}
              onChange={e => setGroupName(e.target.value.trim())}
              placeholder="请输入分组名称"
              maxLength={128}
            />
          </Col>
        </Row>
      </Modal>
    </div>
  )
}

export default KeyWordItem
